'use client';

import React, { useState } from 'react';
import { RefreshCw, AlertTriangle, ArrowUpRight, ArrowDownRight, Globe, ShieldAlert, Database } from 'lucide-react';
import { colors, baseStyles } from './SharedUI';

const formatUSD = (val) => {
  if (val >= 1000000000) return `$${(val / 1000000000).toFixed(2)}B`;
  if (val >= 1000000) return `$${(val / 1000000).toFixed(2)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(1)}k`;
  return `$${val.toFixed(2)}`;
};

export default function MultiExchangeSentimentAnalyzer({ defaultSymbol = 'BTCUSDT' }) {
  const [symbol, setSymbol] = useState(defaultSymbol);
  const [inputSymbol, setInputSymbol] = useState(defaultSymbol);
  const [isLoading, setIsLoading] = useState(false);
  const [exchangeData, setExchangeData] = useState([]);
  const [divergenceWarning, setDivergenceWarning] = useState(null);

  const fetchMultiExchangeData = async (targetSymbol) => {
    setIsLoading(true);
    const cleanSym = targetSymbol.trim().toUpperCase();

    try {
      const res = await fetch(`/api/coinalyze?symbol=${cleanSym}`);
      const json = await res.json();

      if (!json.success) {
        throw new Error(json.error || 'Lỗi lấy dữ liệu từ server proxy');
      }

      const { oiRes, frRes, lsRes, exchangeMap } = json.data;

      const results = Object.keys(exchangeMap).map(exchangeName => {
        const code = exchangeMap[exchangeName];
        const matchSym = `${cleanSym}_PERP.${code}`;
        
        const oiObj = (Array.isArray(oiRes) ? oiRes : []).find(item => item.symbol === matchSym) || {};
        const frObj = (Array.isArray(frRes) ? frRes : []).find(item => item.symbol === matchSym) || {};
        
        const lsObj = (Array.isArray(lsRes) ? lsRes : []).find(item => item.symbol === matchSym) || {};
        const historyList = Array.isArray(lsObj.history) ? lsObj.history : [];
        const latestLS = historyList.length > 0 ? historyList[historyList.length - 1] : { l: 50, s: 50 };

        const longVal = Number(latestLS.l) || 50;
        const shortVal = Number(latestLS.s) || (100 - longVal);

        return {
          exchange: exchangeName,
          openInterest: oiObj.value || 0,
          fundingRate: frObj.value !== undefined ? Number(frObj.value) * 100 : null,
          longRatio: parseFloat(longVal.toFixed(1)),
          shortRatio: parseFloat(shortVal.toFixed(1))
        };
      });

      // ❌ ĐÃ GỠ BỎ .filter(item => item.openInterest > 0) ĐỂ HIỂN THỊ ĐỦ TẤT CẢ CÁC SÀN
      setExchangeData(results);
      analyzeDivergence(results);

    } catch (err) {
      console.error(err);
      alert(`Không thể tải dữ liệu: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const analyzeDivergence = (data) => {
    const activeData = data.filter(d => d.openInterest > 0 && d.fundingRate !== null);
    if (activeData.length === 0) return;

    let hasPositiveFR = activeData.some(d => d.fundingRate > 0.01);
    let hasNegativeFR = activeData.some(d => d.fundingRate < 0);
    let maxLong = Math.max(...activeData.map(d => d.longRatio));
    let minLong = Math.min(...activeData.map(d => d.longRatio));

    if (hasPositiveFR && hasNegativeFR) {
      setDivergenceWarning({
        type: 'CRITICAL',
        text: 'CẢNH BÁO LỆCH PHA NẶNG: Funding Rate giữa các sàn đối lập nhau. Rủi ro quét thanh lý cực cao!'
      });
    } else if (maxLong - minLong > 15) {
      setDivergenceWarning({
        type: 'WARNING',
        text: `Phân kỳ tâm lý lớn: Chênh lệch tỷ lệ Long giữa các sàn lên tới ${(maxLong - minLong).toFixed(1)}%. Cẩn trọng bẫy gồng lỗ.`
      });
    } else {
      setDivergenceWarning({
        type: 'STABLE',
        text: 'Tâm lý dòng tiền và Funding Rate giữa các sàn đang đồng thuận ổn định.'
      });
    }
  };

  const handleSearch = () => {
    if (!inputSymbol.trim()) return;
    const target = inputSymbol.trim().toUpperCase();
    setSymbol(target);
    fetchMultiExchangeData(target);
  };

  return (
    <div style={{ backgroundColor: '#0b0e14', color: '#ffffff', minHeight: '100vh', padding: '20px', boxSizing: 'border-box', fontFamily: 'sans-serif' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        
        {/* HEADER */}
        <div style={{ ...baseStyles.card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', border: '1px solid #334155', flexWrap: 'wrap', gap: '15px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', color: '#ffffff', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Globe size={24} color="#3b82f6" /> TOP EXCHANGES & DEX SENTIMENT ANALYZER ({symbol})
            </h1>
            <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#94a3b8' }}>
              Hiển thị toàn bộ các sàn được cấu hình để kiểm tra dữ liệu trực quan.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input 
              type="text" 
              value={inputSymbol} 
              onChange={(e) => setInputSymbol(e.target.value)} 
              placeholder="BTCUSDT"
              style={{ ...baseStyles.input, width: '100px', textTransform: 'uppercase', color: '#fff', fontWeight: 'bold' }} 
            />
            <button 
              onClick={handleSearch}
              disabled={isLoading}
              style={{ backgroundColor: '#2563eb', color: '#fff', border: 'none', padding: '10px 14px', borderRadius: '6px', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} /> {isLoading ? 'Đang quét...' : 'Phân tích đa sàn'}
            </button>
          </div>
        </div>

        {/* CẢNH BÁO LỆCH PHA */}
        {divergenceWarning && (
          <div style={{ 
            backgroundColor: divergenceWarning.type === 'CRITICAL' ? 'rgba(239, 68, 68, 0.15)' : divergenceWarning.type === 'WARNING' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(34, 197, 94, 0.1)',
            border: `1px solid ${divergenceWarning.type === 'CRITICAL' ? '#ef4444' : divergenceWarning.type === 'WARNING' ? '#f59e0b' : '#22c55e'}`,
            padding: '12px 16px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '12px'
          }}>
            <ShieldAlert size={22} color={divergenceWarning.type === 'CRITICAL' ? '#ef4444' : divergenceWarning.type === 'WARNING' ? '#f59e0b' : '#22c55e'} />
            <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#fff' }}>
              {divergenceWarning.text}
            </div>
          </div>
        )}

        {/* BẢNG SO SÁNH ĐA SÀN */}
        <div style={{ ...baseStyles.card, display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <strong style={{ fontSize: '14px', color: '#fbbf24', borderBottom: '1px solid #334155', paddingBottom: '8px' }}>
            📊 BẢNG ĐỐI SOÁC TẤT CẢ CÁC SÀN ĐƯỢC CẤU HÌNH
          </strong>

          {exchangeData.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <Database size={36} color="#475569" />
              <span>Chưa có dữ liệu. Bấm nút <strong style={{ color: '#3b82f6' }}>"Phân tích đa sàn"</strong> để quét thông tin.</span>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #334155', color: '#94a3b8' }}>
                    <th style={{ padding: '10px' }}>SÀN GIAO DỊCH (EXCHANGE)</th>
                    <th style={{ padding: '10px' }}>OPEN INTEREST (OI)</th>
                    <th style={{ padding: '10px' }}>FUNDING RATE</th>
                    <th style={{ padding: '10px' }}>TỶ LỆ LONG / SHORT</th>
                    <th style={{ padding: '10px' }}>TRẠNG THÁI CHỦ ĐẠO</th>
                  </tr>
                </thead>
                <tbody>
                  {exchangeData.map((item, index) => {
                    const hasData = item.openInterest > 0 || item.fundingRate !== null;
                    const isFundingPositive = item.fundingRate >= 0;
                    const isLongDominant = item.longRatio >= 50;

                    return (
                      <tr key={index} style={{ borderBottom: '1px solid #1e293b', backgroundColor: index % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent', opacity: hasData ? 1 : 0.5 }}>
                        <td style={{ padding: '12px', fontWeight: 'bold', color: '#ffffff', fontSize: '14px' }}>
                          {item.exchange} {item.exchange === 'Hyperliquid' && <span style={{ fontSize: '10px', backgroundColor: 'rgba(59, 130, 246, 0.2)', color: '#60a5fa', padding: '2px 4px', borderRadius: '4px' }}>DEX</span>}
                        </td>
                        <td style={{ padding: '12px', fontWeight: 'bold', color: hasData ? '#38bdf8' : '#64748b', fontFamily: 'monospace' }}>
                          {hasData ? `${item.openInterest.toLocaleString()} HĐ` : 'Không có dữ liệu'}
                        </td>
                        <td style={{ padding: '12px', fontWeight: 'bold', color: !hasData ? '#64748b' : (isFundingPositive ? '#22c55e' : '#ef4444'), fontFamily: 'monospace' }}>
                          {hasData ? (isFundingPositive ? `+${item.fundingRate.toFixed(4)}%` : `${item.fundingRate.toFixed(4)}%`) : '-'}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {hasData ? (
                            <>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: 'bold', marginBottom: '4px' }}>
                                <span style={{ color: '#22c55e' }}>Long: {item.longRatio}%</span>
                                <span style={{ color: '#ef4444' }}>Short: {item.shortRatio}%</span>
                              </div>
                              <div style={{ width: '100%', height: '8px', backgroundColor: '#334155', borderRadius: '4px', overflow: 'hidden', display: 'flex' }}>
                                <div style={{ width: `${item.longRatio}%`, backgroundColor: '#22c55e' }}></div>
                                <div style={{ width: `${item.shortRatio}%`, backgroundColor: '#ef4444' }}></div>
                              </div>
                            </>
                          ) : (
                            <span style={{ color: '#64748b', fontSize: '11px' }}>-</span>
                          )}
                        </td>
                        <td style={{ padding: '12px', fontWeight: 'bold' }}>
                          {hasData ? (
                            isFundingPositive && isLongDominant ? (
                              <span style={{ color: '#22c55e', display: 'flex', alignItems: 'center', gap: '4px' }}><ArrowUpRight size={14}/> Long Hưng Phấn</span>
                            ) : !isFundingPositive && !isLongDominant ? (
                              <span style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: '4px' }}><ArrowDownRight size={14}/> Short Áp Đảo</span>
                            ) : (
                              <span style={{ color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '4px' }}><AlertTriangle size={14}/> Giằng Co / Lệch Pha</span>
                            )
                          ) : (
                            <span style={{ color: '#64748b' }}>Không khả dụng</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
